/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { createContext, type Dispatch, useContext } from 'react';
import type { DesktopRouterLocation } from './navigation/DesktopRouter';
import type { DesktopApp } from './types';
import type { DesktopState, DesktopWindowAction, DesktopWindowState } from './windowSystem';

export interface OpenDesktopPathOptions {
  replace?: boolean;
  /** Window the navigation started in. It keeps the page when its app claims the path. */
  sourceWindowId?: string;
  sourceTabId?: string;
  state?: unknown;
}

export interface DesktopContextValue {
  desktopState: DesktopState;
  dispatchDesktop: Dispatch<DesktopWindowAction>;
  /** Location a window renders, including the selected project for project-scoped apps. */
  getWindowLocation: (windowState: DesktopWindowState, app: DesktopApp) => DesktopRouterLocation;
  /** The saved session was restored (or could not load). Window content mounts only after it. */
  isSessionReady: boolean;
  /** Opens the page at `path` in the window of the app that owns it. */
  openPath: (path: string, options?: OpenDesktopPathOptions) => void;
  selectedProjectId: string | null;
  selectProject: (projectId: string | null) => void;
  setShowWidgets: Dispatch<React.SetStateAction<boolean>>;
  showWidgets: boolean;
  visibleApps: DesktopApp[];
}

/**
 * The stable part of the desktop: actions plus a getter for the latest state. Its value changes
 * only with permissions (`visibleApps`), so consumers don't re-render on every window change.
 */
export type DesktopActions = Pick<
  DesktopContextValue,
  'dispatchDesktop' | 'openPath' | 'selectProject' | 'visibleApps'
> & {
  /** The latest desktop state, for event handlers; read it when acting, not while rendering. */
  getDesktopState: () => DesktopState;
};

export const DesktopContext = createContext<DesktopContextValue | null>(null);
export const DesktopActionsContext = createContext<DesktopActions | null>(null);

/** Everything, re-rendering on every desktop change. Prefer `useDesktopActions` where it's enough. */
export function useDesktop() {
  const context = useContext(DesktopContext);
  if (!context) {
    throw new Error('useDesktop must be used within a DesktopProvider');
  }
  return context;
}

export function useDesktopActions() {
  const context = useContext(DesktopActionsContext);
  if (!context) {
    throw new Error('useDesktopActions must be used within a DesktopProvider');
  }
  return context;
}
